import React, { useEffect, useRef, useState } from 'react';
import colors from "../../utils/colors";

const DataInput = ({
    value = '',
    onSelect,
    initialData = null,
    data = [],
    handleChange,
    placeholder,
    autoSelect = false,
    label = 'label',
    id = 'id',
    disabled = false,
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState(Array.isArray(data) ? data : []);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [isInputDisabled, setIsInputDisabled] = useState(false);

    const debounceTimeout = useRef(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Keep initial-value resolution and selection notifications separate.
    // Some legacy screens pass only an ID in initialData and expect the
    // autocomplete to resolve the complete object through handleChange().
    const lastInitialLookupKeyRef = useRef(null);
    const pendingInitialLookupKeyRef = useRef(null);
    const initialLookupDataRef = useRef(null);
    const lastNotifiedSelectionKeyRef = useRef(null);
    const lastAutoSelectedKeyRef = useRef(null);

    const initialKey = initialData?.[id] ?? null;
    const initialLabel = initialData?.[label] ?? '';
    const hasInitialKey = initialKey !== null && initialKey !== undefined && initialKey !== '';

    const notifySelectOnce = (item) => {
        if (!item || !onSelect) return;

        const itemKey = item?.[id];
        const notifyKey = itemKey === null || itemKey === undefined || itemKey === ''
            ? null
            : String(itemKey);

        if (notifyKey !== null && lastNotifiedSelectionKeyRef.current === notifyKey) {
            return;
        }

        lastNotifiedSelectionKeyRef.current = notifyKey;
        onSelect(item);
    };

    // Preserve the legacy initialData behavior, but key it by a stable scalar
    // so a parent rerender/new object instance cannot create an API loop.
    // The old component always asked handleChange({ id }) to resolve initialData;
    // several contract screens rely on that to turn an ID-only/summary object
    // into the full agent/contract object.
    useEffect(() => {
        if (!hasInitialKey) {
            if (lastInitialLookupKeyRef.current !== null || pendingInitialLookupKeyRef.current !== null) {
                lastInitialLookupKeyRef.current = null;
                pendingInitialLookupKeyRef.current = null;
                initialLookupDataRef.current = null;
                lastNotifiedSelectionKeyRef.current = null;
                lastAutoSelectedKeyRef.current = null;
                setSelectedTags([]);
                setInputValue('');
                setIsInputDisabled(false);
                setShowSuggestions(false);
            }
            return;
        }

        const key = String(initialKey);
        setSelectedTags([initialData]);
        setInputValue('');
        setIsInputDisabled(true);
        setShowSuggestions(false);
        lastAutoSelectedKeyRef.current = key;
        notifySelectOnce(initialData);

        // Resolve the initial value once per scalar key. This intentionally
        // preserves the previous component contract without the infinite loop.
        if (handleChange && lastInitialLookupKeyRef.current !== key) {
            lastInitialLookupKeyRef.current = key;
            pendingInitialLookupKeyRef.current = key;
            initialLookupDataRef.current = data;
            handleChange({ id: initialKey });
        }
        // initialData object identity, handleChange and onSelect are intentionally
        // excluded: many parents recreate them on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialKey, initialLabel, id, label, hasInitialKey]);

    useEffect(() => {
        const nextSuggestions = Array.isArray(data) ? data : [];
        setSuggestions(nextSuggestions);

        const lookupKey = pendingInitialLookupKeyRef.current;
        if (!lookupKey || nextSuggestions.length === 0) return;

        // Do not resolve against stale suggestions that existed before the
        // initial lookup started. Wait until the parent publishes the lookup
        // response as a new data array.
        if (data === initialLookupDataRef.current) return;

        const resolvedItem = nextSuggestions.find((item) => String(item?.[id]) === lookupKey)
            || nextSuggestions.find((item) => String(item?.agentId) === lookupKey)
            || (nextSuggestions.length === 1 ? nextSuggestions[0] : null);

        if (!resolvedItem) return;

        const resolvedKey = resolvedItem?.[id];
        pendingInitialLookupKeyRef.current = null;
        initialLookupDataRef.current = null;
        setSelectedTags([resolvedItem]);
        setInputValue('');
        setIsInputDisabled(true);
        setShowSuggestions(false);
        lastAutoSelectedKeyRef.current = resolvedKey == null ? null : String(resolvedKey);

        // The legacy component notified onSelect once for initialData and again
        // after its lookup returned one/full matching record. Some screens use
        // that second callback to replace a summary object with full data.
        if (onSelect) {
            onSelect(resolvedItem);
        }
        if (resolvedKey !== null && resolvedKey !== undefined && resolvedKey !== '') {
            lastNotifiedSelectionKeyRef.current = String(resolvedKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, id]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Auto-select must never happen during render. Run it as a guarded effect
    // only after a real user search has opened the suggestion list.
    useEffect(() => {
        if (!autoSelect || disabled || isInputDisabled || !showSuggestions || suggestions.length !== 1) {
            return;
        }

        const item = suggestions[0];
        const itemKey = item?.[id];
        if (itemKey === null || itemKey === undefined || itemKey === '') {
            return;
        }

        const key = String(itemKey);
        if (lastAutoSelectedKeyRef.current === key) {
            return;
        }

        lastAutoSelectedKeyRef.current = key;
        setSelectedTags([item]);
        setInputValue('');
        setIsInputDisabled(true);
        setShowSuggestions(false);
        notifySelectOnce(item);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSelect, disabled, id, isInputDisabled, showSuggestions, suggestions]);

    useEffect(() => () => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
    }, []);

    const handleChangeIn = (e) => {
        const text = e.target.value;
        setInputValue(text);
        lastAutoSelectedKeyRef.current = null;

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (text.trim() === '') {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setShowSuggestions(true);
        debounceTimeout.current = setTimeout(() => {
            if (handleChange) {
                handleChange(text);
            }
        }, 1000);
    };

    const handleSelect = (item) => {
        if (!item) return;

        const itemKey = item?.[id];
        const alreadySelected = selectedTags.some(
            (tag) => String(tag?.[id]) === String(itemKey)
        );
        if (alreadySelected) {
            setShowSuggestions(false);
            return;
        }

        setSelectedTags([item]);
        setInputValue('');
        setIsInputDisabled(true);
        setShowSuggestions(false);
        lastAutoSelectedKeyRef.current = itemKey == null ? null : String(itemKey);
        notifySelectOnce(item);
    };

    const removeTag = (itemKey) => {
        setSelectedTags((current) => current.filter(
            (tag) => String(tag?.[id]) !== String(itemKey)
        ));
        setInputValue('');
        setIsInputDisabled(false);
        setShowSuggestions(false);
        lastInitialLookupKeyRef.current = null;
        pendingInitialLookupKeyRef.current = null;
        initialLookupDataRef.current = null;
        lastNotifiedSelectionKeyRef.current = null;
        lastAutoSelectedKeyRef.current = null;

        if (onSelect) {
            onSelect(null);
        }

        requestAnimationFrame(() => inputRef.current?.focus());
    };

    return (
        <div ref={containerRef} style={{ position: 'relative' }}>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    border: isInputDisabled && !disabled
                        ? '1px solid var(--primary-color)'
                        : disabled
                            ? '1px solid var(--border-color)'
                            : '',
                    borderRadius: '4px',
                    minHeight: '45px',
                    cursor: disabled ? 'default' : 'text',
                }}
            >
                {selectedTags.map((tag) => (
                    tag?.[id] ? (
                        <div
                            key={tag[id]}
                            style={{
                                background: disabled ? colors.border : colors.primary,
                                borderRadius: '4px',
                                padding: '4px 8px',
                                display: 'flex',
                                marginLeft: '4px',
                                alignItems: 'center',
                                marginRight: '4px',
                                color: colors.white,
                            }}
                        >
                            <span>{tag[id]} - {tag[label]}</span>
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => removeTag(tag[id])}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        marginLeft: '8px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        color: colors.white,
                                    }}
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ) : null
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    style={{ display: isInputDisabled ? 'none' : '' }}
                    className="form-control-uld"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleChangeIn}
                    onFocus={() => {
                        if (inputValue.trim() && suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    autoComplete="off"
                    disabled={isInputDisabled || disabled}
                />
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <ul
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 999,
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        listStyle: 'none',
                        margin: 0,
                        padding: 0,
                    }}
                >
                    {suggestions.map((item) => (
                        <li
                            key={item?.[id]}
                            style={{ padding: '8px', cursor: 'pointer' }}
                            onClick={() => handleSelect(item)}
                        >
                            {item?.[id]} - {item?.[label]}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DataInput;
